import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import ClientNav from '@/components/ClientNav';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Send, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientRequestDetail = () => {
  const { id } = useParams();
  const { token, user } = useAuth();
  const [request, setRequest] = useState(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchRequest();
  }, [id]);

  const fetchRequest = async () => {
    try {
      const response = await axios.get(`${API}/client/requests/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequest(response.data.request);
    } catch (error) {
      console.error('Failed to fetch request:', error);
      toast.error('Failed to load request');
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;

    setLoading(true);
    try {
      await axios.post(`${API}/client/requests/${id}/message`, {
        message: message,
        sender_role: 'client'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Message sent!');
      setMessage('');
      fetchRequest();
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-500/10 text-green-400';
      case 'in_progress': return 'bg-blue-500/10 text-blue-400';
      default: return 'bg-yellow-500/10 text-yellow-400';
    }
  };

  if (!request) {
    return (
      <div className="min-h-screen bg-[#030712]">
        <ClientNav />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030712]" data-testid="request-detail-page">
      <ClientNav />
      
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link
          to="/client/requests"
          className="inline-flex items-center text-slate-400 hover:text-white mb-6 transition-colors"
          data-testid="back-to-requests"
        >
          <ArrowLeft size={18} className="mr-2" />
          Back to Requests
        </Link>

        <div className="bg-slate-950 border border-slate-800 rounded-xl p-8 mb-6" data-testid="request-header">
          <div className="flex items-start justify-between mb-4">
            <h1 className="text-3xl font-bold font-['Syne'] text-white" data-testid="request-title">{request.title}</h1>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(request.status)}`} data-testid="request-status">
              {request.status.replace('_', ' ')}
            </span>
          </div>
          <p className="text-slate-400 mb-4" data-testid="request-description">{request.description}</p>
          <span className="text-sm text-slate-500">
            Created on {format(new Date(request.created_at), 'MMMM dd, yyyy')}
          </span>
        </div>

        <div className="bg-slate-950 border border-slate-800 rounded-xl p-8" data-testid="messages-section">
          <h2 className="text-xl font-bold font-['Syne'] text-white mb-6">Messages</h2>
          
          <div className="space-y-4 mb-6" data-testid="messages-list">
            {request.messages && request.messages.length > 0 ? (
              request.messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`p-4 rounded-lg ${
                    msg.sender_role === 'client'
                      ? 'bg-indigo-500/10 border border-indigo-500/20 ml-8'
                      : 'bg-slate-900 border border-slate-800 mr-8'
                  }`}
                  data-testid={`message-${msg.id}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-white">
                      {msg.sender_role === 'client' ? 'You' : 'Admin'}
                    </span>
                    <span className="text-xs text-slate-500">
                      {format(new Date(msg.created_at), 'MMM dd, h:mm a')}
                    </span>
                  </div>
                  <p className="text-slate-300">{msg.message}</p>
                </div>
              ))
            ) : (
              <p className="text-slate-500 text-center py-8" data-testid="no-messages">No messages yet. Start the conversation!</p>
            )}
          </div>

          <form onSubmit={handleSendMessage} data-testid="message-form">
            <div className="flex gap-3">
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Type your message..."
                className="flex-1 bg-slate-900 border-slate-800 text-white resize-none"
                rows={3}
                data-testid="message-input"
              />
              <Button
                type="submit"
                disabled={loading || !message.trim()}
                className="bg-indigo-500 hover:bg-indigo-600 text-white self-end"
                data-testid="send-message-btn"
              >
                <Send size={18} />
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ClientRequestDetail;
